/**
 * JOHero — Cinematic full-bleed hero. 100% data-driven from `settings.universe_<name>`.
 *
 * Reads from settings (per universe):
 *  - hero_eyebrow, hero_subtitle, hero_images[], hero_iframe_url
 *  - hero_badges: [{ label, icon }]
 *  - hero_cta_primary: { label, route }
 *  - hero_cta_secondary: { label, route }
 *  - hero_cc_cards: [{ title, text, icon, route }]  (optional, e-commerce)
 *
 * NON-CORE FILE — additive customization.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";
import { useSettings } from "@/lib/useSettings";
import JORotatingCity from "@/components/jardins-ole/JORotatingCity";
import { Icon as DynIcon } from "@/lib/icons";
import { ArrowRight } from "lucide-react";

export default function JOHero() {
  const { universe, config } = useUniverse();
  const { settings: s } = useSettings();
  const [idx, setIdx] = useState(0);

  const images = Array.isArray(config?.hero_images) ? config.hero_images.filter(Boolean) : [];
  const eyebrow = config?.hero_eyebrow || "";
  const subtitle = config?.hero_subtitle || s?.hero_subtitle || "";
  const heroLeadIn = config?.hero_lead_in || (universe === "jardinerie" ? "Votre jardinerie" : "Paysagistes");
  const cities = s?.intervention_zones_list;
  const badges = Array.isArray(config?.hero_badges) ? config.hero_badges : [];
  const ctaPrimary = config?.hero_cta_primary || null;
  const ctaSecondary = config?.hero_cta_secondary || null;
  const ccCards = Array.isArray(config?.hero_cc_cards) ? config.hero_cc_cards : [];

  // Preload all carousel images
  useEffect(() => {
    images.forEach((src) => { const i = new Image(); i.src = src; });
  }, [images]);

  // Auto-advance every 6s
  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 6000);
    return () => clearInterval(t);
  }, [images.length]);

  useEffect(() => { setIdx(0); }, [universe]);

  return (
    <section className="jo-hero" data-testid="jo-hero" data-universe={universe}>
      <div className="jo-hero-carousel" aria-hidden="true">
        {images.map((src, i) => (
          <img
            key={`${universe}-${i}`}
            src={src}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            fetchpriority={i === 0 ? "high" : "auto"}
            className={`jo-hero-slide-img ${i === idx ? "is-active" : ""}`}
            data-testid={`jo-hero-slide-${i}`}
          />
        ))}
      </div>
      <div className="jo-hero-overlay" />
      <div className="jo-hero-grain" />

      <div className="jo-hero-content">
        <div className="jo-container py-12 lg:py-24">
          <div className="grid grid-cols-12 gap-4 lg:gap-10 items-center">
            <div className="col-span-12 max-w-5xl">
              {eyebrow && <div className="jo-eyebrow mb-3">{eyebrow}</div>}
              <h1 className="jo-hero-title text-[clamp(1.1rem,4.6vw,4.25rem)] mb-6 leading-[1.15]">
                <span className="whitespace-nowrap">
                  {heroLeadIn}&nbsp;à&nbsp;<JORotatingCity cities={cities} />
                </span>
              </h1>
              {subtitle && (
                <p className="text-lg lg:text-xl jo-hero-sub max-w-2xl mb-8 leading-relaxed">
                  {subtitle}
                </p>
              )}

              {badges.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="jo-hero-badges">
                  {badges.map((b, i) => (
                    <span
                      key={i}
                      className={`jo-badge ${b.style === "gold" ? "jo-badge--gold" : b.style === "blue" ? "jo-badge--blue" : ""}`}
                    >
                      {b.icon && <DynIcon name={b.icon} className="w-3.5 h-3.5" />}
                      {b.label}
                    </span>
                  ))}
                </div>
              )}

              {(ctaPrimary?.label || ctaSecondary?.label) && (
                <div className="flex flex-wrap items-center gap-3 mt-7">
                  {ctaPrimary?.label && (
                    <Link to={ctaPrimary.route || "#"} className="jo-btn-primary" data-testid="jo-hero-cta-primary">
                      {ctaPrimary.label}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                  {ctaSecondary?.label && (
                    <Link to={ctaSecondary.route || "#"} className="jo-btn-secondary" data-testid="jo-hero-cta-secondary">
                      {ctaSecondary.label}
                    </Link>
                  )}
                </div>
              )}

              {ccCards.length > 0 && (
                <div className="jo-cc-cluster" data-testid="jo-hero-cc-cluster">
                  {ccCards.map((c, i) => (
                    <Link key={i} to={c.route || "#"} className="jo-cc-card" data-testid={`jo-hero-cc-${i}`}>
                      <span className="jo-cc-icon">{c.icon && <DynIcon name={c.icon} className="w-4 h-4" strokeWidth={2.4} />}</span>
                      <div>
                        <div className="jo-cc-title">{c.title}</div>
                        <div className="jo-cc-text">{c.text}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {images.length > 1 && (
        <div className="jo-hero-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className={`jo-hero-dot ${i === idx ? "is-active" : ""}`}
              aria-label={`Image ${i + 1}`}
              data-testid={`jo-hero-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
